import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  checkSourceWatchSource,
  createDiagramAsset,
  createDiagramAssetVersion,
  createLatestSafetyUpdate,
  createMaterialVariant,
  createSafetyMaster,
  createSafetyMasterRevision,
  createSafetyTopic,
  createSourceWatchSource,
  createThemeKnowledgePack,
  createThemeKnowledgePackVersion,
  createTrainingModuleContent,
  createTrainingModuleVersion,
  listDiagramAssetVersions,
  listCatalog,
  listLatestSafetyUpdates,
  listMaterialVariants,
  listMediaLibrary,
  listSafetyMasterRevisions,
  listSafetyTopics,
  listSourcePermissionDecisions,
  listSourceWatchBatches,
  listSourceWatchItems,
  listSourceWatchSources,
  listThemeKnowledgePackVersions,
  listTrainingModuleContent,
  listVariantCoverage,
  mediaLibraryAssetUrl,
  resumeSourceWatchSource,
  transitionDiagramAssetVersion,
  transitionLatestSafetyUpdate,
  transitionMaterialVariant,
  transitionMediaLibraryAsset,
  transitionSafetyMasterRevision,
  transitionSafetyTopic,
  transitionSourceWatchSource,
  transitionThemeKnowledgePackVersion,
  transitionTrainingModuleVersion,
  uploadMediaLibraryAsset,
  type ContentStatus,
  type CatalogItem,
  type DiagramAssetVersion,
  type DiagramAssetVersionInput,
  type LatestSafetyUpdate,
  type MaterialVariant,
  type MediaLibraryAsset,
  type SafetyMasterRevision,
  type SafetyTopic,
  type SourcePermissionDecision,
  type SourceWatchItem,
  type SourceWatchBatch,
  type SourceWatchSource,
  type ThemeKnowledgePackVersion,
  type ThemeKnowledgePackVersionInput,
  type TrainingModule,
  type TrainingModuleVersion,
  type VariantCoverage,
} from './api';
import { buildModuleTestQuestions } from './module-test-questions';
import { SourceReviewPanel } from './SourceReviewPanel';
import { SourcePermissionPanel } from './SourcePermissionPanel';

type Props = {
  csrfToken: string;
  onError: (error: unknown) => void;
};

type Section =
  | 'diagrams'
  | 'knowledge'
  | 'latest'
  | 'media'
  | 'modules'
  | 'safety'
  | 'source-watch'
  | 'topics'
  | 'variants';

const today = new Date().toISOString().slice(0, 10);
const nextYear = `${Number(today.slice(0, 4)) + 1}${today.slice(4)}`;

const knowledgePackSections = [
  ['ACCIDENT_PATTERN', '代表的な事故パターン'],
  ['PRIMARY_HAZARD', '主要危険'],
  ['ROOT_CAUSE', '根本原因'],
  ['HUMAN_FACTOR', '人的要因'],
  ['EQUIPMENT_FACTOR', '機械要因'],
  ['ENVIRONMENT_FACTOR', '環境要因'],
  ['PRE_WORK_CHECK', '作業前確認'],
  ['DURING_WORK_RULE', '作業中ルール'],
  ['STOP_WORK_CONDITION', '作業中止条件'],
  ['ROLE_DRIVER', '運転者の役割'],
  ['ROLE_SPOTTER', '誘導員の役割'],
  ['ROLE_WORKER', '周囲作業員の役割'],
  ['GOOD_EXAMPLE', '良い例'],
  ['BAD_EXAMPLE', '悪い例'],
  ['DIAGRAM_REQUIREMENT', '必要な図解'],
  ['RECOMMENDED_VISUAL', '推奨ビジュアル'],
] as const satisfies ReadonlyArray<
  readonly [ThemeKnowledgePackVersionInput['items'][number]['section'], string]
>;

function knowledgePackVersionInput(
  pack: ThemeKnowledgePackVersion,
): ThemeKnowledgePackVersionInput {
  return {
    items: pack.items,
    kyScenarios: pack.kyScenarios,
    quizBank: pack.quizBank,
    sources: pack.sources,
    themeOverview: pack.themeOverview,
    title: pack.title,
    variations: pack.variations,
    whyItMatters: pack.whyItMatters,
  };
}

function diagramVersionInput(asset: DiagramAssetVersion): DiagramAssetVersionInput {
  return {
    altText: asset.altText,
    annotationAnchors: asset.annotationAnchors,
    aspectRatio: asset.aspectRatio,
    assetPath: asset.assetPath,
    diagramType: asset.diagramType,
    purpose: asset.purpose,
    rightsNote: asset.rightsNote,
    sourceUrl: asset.sourceUrl,
    title: asset.title,
    variant: asset.variant,
  };
}

function text(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

function nullable(data: FormData, name: string): string | null {
  return text(data, name) || null;
}

function lines(data: FormData, name: string): string[] {
  return text(data, name)
    .split(/\r?\n/u)
    .map((value) => value.trim())
    .filter(Boolean);
}

function nextStatus(status: ContentStatus): ContentStatus | null {
  if (status === 'DRAFT') return 'IN_REVIEW';
  if (status === 'IN_REVIEW') return 'APPROVED';
  if (status === 'APPROVED') return 'RETIRED';
  return null;
}

function statusLabel(status: ContentStatus): string {
  const labels: Record<ContentStatus, string> = {
    APPROVED: '承認済み',
    DRAFT: '下書き',
    IN_REVIEW: '確認中',
    RETIRED: '廃止',
  };
  return labels[status];
}

function actionLabel(status: ContentStatus): string {
  if (status === 'IN_REVIEW') return '確認へ回す';
  if (status === 'APPROVED') return '承認する';
  return '廃止する';
}

function StatusAction({
  busy,
  onAction,
  status,
}: {
  busy: boolean;
  onAction: (status: ContentStatus) => Promise<void>;
  status: ContentStatus;
}) {
  const next = nextStatus(status);
  return (
    <div className="content-status-actions">
      <span className={`status-chip ${status.toLowerCase()}`}>{statusLabel(status)}</span>
      {next !== null && (
        <button
          className={next === 'RETIRED' ? 'danger-link' : 'secondary compact'}
          disabled={busy}
          onClick={() => void onAction(next)}
          type="button"
        >
          {actionLabel(next)}
        </button>
      )}
    </div>
  );
}

export function ContentLibraryPanel({ csrfToken, onError }: Props) {
  const [section, setSection] = useState<Section>('topics');
  const [sourceWatchView, setSourceWatchView] = useState<'permission' | 'registry' | 'review'>(
    'review',
  );
  const [topics, setTopics] = useState<SafetyTopic[]>([]);
  const [safety, setSafety] = useState<SafetyMasterRevision[]>([]);
  const [latest, setLatest] = useState<LatestSafetyUpdate[]>([]);
  const [sourceWatchSources, setSourceWatchSources] = useState<SourceWatchSource[]>([]);
  const [sourceWatchBatches, setSourceWatchBatches] = useState<SourceWatchBatch[]>([]);
  const [sourceWatchItems, setSourceWatchItems] = useState<SourceWatchItem[]>([]);
  const [sourcePermissionDecisions, setSourcePermissionDecisions] = useState<
    SourcePermissionDecision[]
  >([]);
  const [workTypes, setWorkTypes] = useState<CatalogItem[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<CatalogItem[]>([]);
  const [knowledgePacks, setKnowledgePacks] = useState<ThemeKnowledgePackVersion[]>([]);
  const [diagramAssets, setDiagramAssets] = useState<DiagramAssetVersion[]>([]);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [moduleVersions, setModuleVersions] = useState<TrainingModuleVersion[]>([]);
  const [variants, setVariants] = useState<MaterialVariant[]>([]);
  const [coverage, setCoverage] = useState<VariantCoverage[]>([]);
  const [media, setMedia] = useState<MediaLibraryAsset[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const masters = useMemo(
    () =>
      [...new Map(safety.map((item) => [item.masterId, item])).values()].sort((a, b) =>
        a.code.localeCompare(b.code),
      ),
    [safety],
  );

  async function refresh() {
    const [
      nextTopics,
      nextSafety,
      nextLatest,
      nextSourceWatchSources,
      nextSourceWatchBatches,
      nextSourceWatchItems,
      nextSourcePermissionDecisions,
      nextWorkTypes,
      nextEquipmentTypes,
      nextKnowledgePacks,
      nextDiagramAssets,
      moduleContent,
      nextVariants,
      nextCoverage,
      nextMedia,
    ] = await Promise.all([
      listSafetyTopics(),
      listSafetyMasterRevisions(),
      listLatestSafetyUpdates(),
      listSourceWatchSources(),
      listSourceWatchBatches(),
      listSourceWatchItems(),
      listSourcePermissionDecisions(),
      listCatalog('work-types'),
      listCatalog('equipment-types'),
      listThemeKnowledgePackVersions(),
      listDiagramAssetVersions(),
      listTrainingModuleContent(),
      listMaterialVariants(),
      listVariantCoverage(),
      listMediaLibrary(),
    ]);
    setTopics(nextTopics);
    setSafety(nextSafety);
    setLatest(nextLatest);
    setSourceWatchSources(nextSourceWatchSources);
    setSourceWatchBatches(nextSourceWatchBatches);
    setSourceWatchItems(nextSourceWatchItems);
    setSourcePermissionDecisions(nextSourcePermissionDecisions);
    setWorkTypes(nextWorkTypes);
    setEquipmentTypes(nextEquipmentTypes);
    setKnowledgePacks(nextKnowledgePacks);
    setDiagramAssets(nextDiagramAssets);
    setModules(moduleContent.modules);
    setModuleVersions(moduleContent.versions);
    setVariants(nextVariants);
    setCoverage(nextCoverage);
    setMedia(nextMedia);
  }

  useEffect(() => {
    void refresh()
      .catch(onError)
      .finally(() => setLoading(false));
  }, [onError]);

  async function run(action: () => Promise<void>, message: string) {
    setBusy(true);
    setNotice(null);
    setFormError(null);
    try {
      await action();
      await refresh();
      setNotice(message);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function submitTopic(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await createSafetyTopic(
        {
          code: text(data, 'code'),
          description: text(data, 'description'),
          name: text(data, 'name'),
          sortOrder: Number(text(data, 'sortOrder')),
        },
        csrfToken,
      );
      form.reset();
    }, '安全テーマを下書きとして登録しました。');
  }

  async function submitSafety(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const revision = {
      body: text(data, 'body'),
      effectiveFrom: text(data, 'effectiveFrom'),
      effectiveTo: nullable(data, 'effectiveTo'),
      issuer: text(data, 'issuer'),
      publishedOn: nullable(data, 'publishedOn'),
      sourceDocument: text(data, 'sourceDocument'),
      sourceUrl: text(data, 'sourceUrl'),
      title: text(data, 'title'),
      topicIds: [text(data, 'topicId')],
      verifiedOn: text(data, 'verifiedOn'),
    };
    await run(async () => {
      const masterId = text(data, 'masterId');
      if (masterId) await createSafetyMasterRevision(masterId, revision, csrfToken);
      else {
        await createSafetyMaster(
          { category: text(data, 'category'), code: text(data, 'code'), revision },
          csrfToken,
        );
      }
      form.reset();
    }, '安全情報を不変revisionとして登録しました。');
  }

  async function submitLatest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await createLatestSafetyUpdate(
        {
          category: text(data, 'category'),
          checkedOn: text(data, 'checkedOn'),
          effectiveFrom: text(data, 'effectiveFrom'),
          issuer: text(data, 'issuer'),
          notes: nullable(data, 'notes'),
          publishedOn: text(data, 'publishedOn'),
          sourceUrl: text(data, 'sourceUrl'),
          summary: text(data, 'summary'),
          title: text(data, 'title'),
          topicIds: [text(data, 'topicId')],
          validUntil: text(data, 'validUntil'),
        },
        csrfToken,
      );
      form.reset();
    }, '最新安全情報を下書きとして登録しました。');
  }

  async function submitSourceWatchSource(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await createSourceWatchSource(
        {
          baseUrl: text(data, 'baseUrl'),
          categories: lines(data, 'categories'),
          checkFrequencyHours: Number(text(data, 'checkFrequencyHours')),
          commercialUseStatus: text(data, 'commercialUseStatus') as
            'CONFIRMED' | 'NEEDS_REVIEW' | 'PROHIBITED' | 'RESTRICTED',
          contentUsagePolicy: text(data, 'contentUsagePolicy') as
            | 'CASE_SOURCE'
            | 'FACT_SOURCE'
            | 'MANUAL_PERMISSION_REQUIRED'
            | 'REFERENCE_ONLY'
            | 'STRUCTURE_REFERENCE',
          issuer: text(data, 'issuer'),
          organization: text(data, 'organization'),
          permissionNotes: nullable(data, 'permissionNotes'),
          permissionRequired: data.get('permissionRequired') === 'on',
          priority: text(data, 'priority') as 'CRITICAL' | 'HIGH' | 'NORMAL' | 'REFERENCE',
          regionScope: text(data, 'regionScope') as
            'HOKKAIDO' | 'MULTI_REGION' | 'NATIONAL' | 'OTHER_REGION',
          registryGroupCode: text(data, 'registryGroupCode'),
          robotsPolicyStatus: text(data, 'robotsPolicyStatus') as
            'ALLOWED' | 'DISALLOWED' | 'MANUAL_REVIEW' | 'UNCONFIRMED',
          sourceClass: text(data, 'sourceClass') as
            | 'INDUSTRY_ASSOCIATION'
            | 'OFFICIAL_GOV'
            | 'OFFICIAL_LOCAL_GOV'
            | 'PROFESSIONAL_ASSOCIATION'
            | 'REFERENCE',
          sourceUrl: text(data, 'sourceUrl'),
          title: text(data, 'title'),
          watchMethod: text(data, 'watchMethod') as
            'ATOM' | 'HTML_HASH' | 'MANUAL_INDEX' | 'PDF_METADATA' | 'RSS',
        },
        csrfToken,
      );
      form.reset();
    }, '公式ソースを下書き登録しました。確認後に承認してください。');
  }

  async function submitKnowledgePack(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    let version: ThemeKnowledgePackVersionInput;
    try {
      const parsed: unknown = JSON.parse(text(data, 'versionJson'));
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('object required');
      }
      version = parsed as ThemeKnowledgePackVersionInput;
    } catch {
      setFormError('Knowledge Pack JSONを正しいJSONオブジェクト形式で入力してください。');
      return;
    }
    await run(async () => {
      const packId = text(data, 'packId');
      if (packId) await createThemeKnowledgePackVersion(packId, version, csrfToken);
      else {
        await createThemeKnowledgePack(
          {
            code: text(data, 'code'),
            topicId: text(data, 'topicId'),
            version,
          },
          csrfToken,
        );
      }
      form.reset();
    }, 'Knowledge Packを不変versionとして下書き登録しました。');
  }

  async function submitDiagramAsset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    let version: DiagramAssetVersionInput;
    try {
      const parsed: unknown = JSON.parse(text(data, 'versionJson'));
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('object required');
      }
      version = parsed as DiagramAssetVersionInput;
    } catch {
      setFormError('図解version JSONを正しいJSONオブジェクト形式で入力してください。');
      return;
    }
    await run(async () => {
      const assetId = text(data, 'assetId');
      if (assetId) await createDiagramAssetVersion(assetId, version, csrfToken);
      else {
        await createDiagramAsset(
          {
            aliases: lines(data, 'aliases'),
            code: text(data, 'code'),
            topicId: text(data, 'topicId'),
            version,
          },
          csrfToken,
        );
      }
      form.reset();
    }, '図解を不変versionとして下書き登録しました。');
  }

  async function submitModule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const publicSource = text(data, 'sourceType') === 'PUBLIC_SOURCE';
    const testQuestionResult = buildModuleTestQuestions(
      [1, 2].map((questionNo) => ({
        correctAnswer: text(data, `testQuestion${questionNo}CorrectAnswer`),
        explanation: text(data, `testQuestion${questionNo}Explanation`),
        question: text(data, `testQuestion${questionNo}Question`),
        wrongAnswers: lines(data, `testQuestion${questionNo}WrongAnswers`),
      })),
    );
    if (testQuestionResult.error !== null) {
      setFormError(testQuestionResult.error);
      return;
    }
    const version = {
      body: text(data, 'body'),
      containsLegalClaim: data.get('containsLegalClaim') === 'on',
      containsNumericClaim: data.get('containsNumericClaim') === 'on',
      effectiveFrom: text(data, 'effectiveFrom'),
      effectiveTo: nullable(data, 'effectiveTo'),
      equipmentTypeIds: [],
      issuer: nullable(data, 'issuer'),
      kyQuestions: lines(data, 'kyQuestions'),
      required: data.get('required') === 'on',
      seasonFrom: null,
      seasonTo: null,
      sourceDocument: nullable(data, 'sourceDocument'),
      sourceType: publicSource ? ('PUBLIC_SOURCE' as const) : ('INTERNAL_EDITORIAL' as const),
      sourceUrl: nullable(data, 'sourceUrl'),
      speakerNotes: text(data, 'speakerNotes'),
      testQuestions: testQuestionResult.questions,
      title: text(data, 'title'),
      topicIds: [text(data, 'topicId')],
      verifiedOn: nullable(data, 'verifiedOn'),
      workTypeIds: [],
    };
    await run(async () => {
      const moduleId = text(data, 'moduleId');
      if (moduleId) await createTrainingModuleVersion(moduleId, version, csrfToken);
      else {
        await createTrainingModuleContent(
          { code: text(data, 'code'), moduleType: text(data, 'moduleType'), version },
          csrfToken,
        );
      }
      form.reset();
    }, '教材モジュールを不変versionとして登録しました。');
  }

  async function submitVariant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    await run(async () => {
      await createMaterialVariant(
        {
          code: text(data, 'code') as MaterialVariant['code'],
          description: text(data, 'description'),
          layoutRules: {
            introductionStyle: text(data, 'introductionStyle'),
            maximumSlides: Number(text(data, 'maximumSlides')),
            minimumSlides: Number(text(data, 'minimumSlides')),
            notesTone: text(data, 'notesTone') as 'DIRECT' | 'FACILITATIVE' | 'CASE_BASED',
            slotOrder: lines(data, 'slotOrder'),
          },
          moduleRules: [],
          name: text(data, 'name'),
          selectionRules: {
            accidentCaseCategories: lines(data, 'accidentCaseCategories'),
            focusRisks: lines(data, 'focusRisks'),
            kyQuestionCategories: lines(data, 'kyQuestionCategories'),
            mediaPolicy: text(data, 'mediaPolicy') as
              'BALANCED' | 'SITE_PHOTO_PRIORITY' | 'ILLUSTRATION_PRIORITY',
            testQuestionCategories: lines(data, 'testQuestionCategories'),
          },
          topicId: text(data, 'topicId'),
        },
        csrfToken,
      );
      form.reset();
    }, '教材パターンを下書きとして登録しました。');
  }

  async function submitMedia(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const selected = data.get('file');
    if (!(selected instanceof File) || selected.size === 0) return;
    await run(async () => {
      await uploadMediaLibraryAsset(
        selected,
        {
          assetType: text(data, 'assetType') as MediaLibraryAsset['assetType'],
          category: text(data, 'category'),
          licenseNotes: text(data, 'licenseNotes'),
          sourceUrl: nullable(data, 'sourceUrl'),
          title: text(data, 'title'),
          topicIds: [text(data, 'topicId')],
        },
        csrfToken,
      );
      form.reset();
    }, '共通素材をprivate R2へ登録しました。内容を確認して承認してください。');
  }

  if (loading) return <div className="card">安全コンテンツを読み込んでいます…</div>;

  const approvedCoverage = coverage.filter((item) => item.missingApprovedCodes.length === 0).length;

  return (
    <section className="content-library">
      <div className="section-heading">
        <div>
          <p className="eyebrow">APPROVED CONTENT LIBRARY</p>
          <h2>安全コンテンツ管理</h2>
          <p className="muted">確認・承認した内容だけが教材生成候補になります。</p>
        </div>
        <div className="content-metrics" aria-label="コンテンツ登録状況">
          <span>テーマ {topics.length}</span>
          <span>安全情報 {safety.length}</span>
          <span>
            公的情報 確認待ち{' '}
            {
              sourceWatchItems.filter((item) => item.administratorStatus === 'PENDING_REVIEW')
                .length
            }
          </span>
          <span>Knowledge Pack {knowledgePacks.length}</span>
          <span>図解 {diagramAssets.length}</span>
          <span>
            A〜E承認済み {approvedCoverage}/{topics.length}
          </span>
        </div>
      </div>

      {notice !== null && <div className="notice success">{notice}</div>}
      {formError !== null && (
        <div aria-live="assertive" className="notice error" role="alert">
          {formError}
        </div>
      )}
      <nav
        aria-label="安全コンテンツメニュー"
        className="subtab-list"
        data-content-library-version="quality-phase-5"
      >
        {(
          [
            ['topics', '安全テーマ'],
            ['safety', '安全情報'],
            ['latest', '最新安全情報'],
            ['source-watch', '公的情報監視'],
            ['knowledge', 'Knowledge Pack'],
            ['diagrams', 'SVG図解'],
            ['modules', '教材モジュール'],
            ['variants', 'A〜Eパターン'],
            ['media', '素材ライブラリ'],
          ] as const
        ).map(([value, label]) => (
          <button
            className={section === value ? 'subtab active' : 'subtab'}
            key={value}
            onClick={() => setSection(value)}
            type="button"
          >
            {label}
          </button>
        ))}
      </nav>

      {section === 'topics' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitTopic(event)}>
            <h3>安全テーマを追加</h3>
            <label>
              テーマコード
              <input name="code" required />
            </label>
            <label>
              テーマ名
              <input name="name" required />
            </label>
            <label>
              説明
              <textarea name="description" required rows={4} />
            </label>
            <label>
              表示順
              <input
                defaultValue={topics.length * 10 + 10}
                min="0"
                name="sortOrder"
                required
                type="number"
              />
            </label>
            <button disabled={busy}>下書き登録</button>
          </form>
          <div className="stack">
            {topics.map((topic) => (
              <article className="card content-row" key={topic.id}>
                <div>
                  <strong>{topic.name}</strong>
                  <small>
                    {topic.code} · {topic.description}
                  </small>
                </div>
                <StatusAction
                  busy={busy}
                  onAction={(status) =>
                    run(async () => {
                      await transitionSafetyTopic(topic.id, status, csrfToken);
                    }, 'テーマの状態を更新しました。')
                  }
                  status={topic.status}
                />
              </article>
            ))}
          </div>
        </div>
      )}

      {section === 'safety' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitSafety(event)}>
            <h3>安全情報・改訂版を登録</h3>
            <label>
              登録先
              <select name="masterId">
                <option value="">新しい安全情報</option>
                {masters.map((item) => (
                  <option key={item.masterId} value={item.masterId}>
                    {item.code}
                  </option>
                ))}
              </select>
            </label>
            <label>
              新規コード
              <input name="code" />
            </label>
            <label>
              カテゴリー
              <input name="category" required />
            </label>
            <label>
              タイトル
              <input name="title" required />
            </label>
            <label>
              本文
              <textarea name="body" required rows={8} />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              発行主体
              <input name="issuer" required />
            </label>
            <label>
              根拠資料
              <input name="sourceDocument" required />
            </label>
            <label>
              出典URL
              <input name="sourceUrl" required type="url" />
            </label>
            <div className="form-grid compact-grid">
              <label>
                公表日
                <input name="publishedOn" type="date" />
              </label>
              <label>
                確認日
                <input defaultValue={today} name="verifiedOn" required type="date" />
              </label>
              <label>
                有効開始
                <input defaultValue={today} name="effectiveFrom" required type="date" />
              </label>
              <label>
                有効終了
                <input defaultValue={nextYear} name="effectiveTo" type="date" />
              </label>
            </div>
            <button disabled={busy}>不変revisionを登録</button>
          </form>
          <ContentRecords
            empty="安全情報は未登録です。"
            records={safety.map((item) => ({
              id: item.id,
              title: `${item.title}（${item.code} rev.${item.revisionNo}）`,
              meta: `${item.issuer} · ${item.effectiveFrom}〜${item.effectiveTo ?? '期限なし'}`,
              status: item.approvalStatus,
              action: (status) =>
                run(async () => {
                  await transitionSafetyMasterRevision(item.masterId, item.id, status, csrfToken);
                }, '安全情報の状態を更新しました。'),
            }))}
            busy={busy}
          />
        </div>
      )}

      {section === 'latest' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitLatest(event)}>
            <h3>公的な最新安全情報を登録</h3>
            <label>
              タイトル
              <input name="title" required />
            </label>
            <label>
              概要
              <textarea name="summary" required rows={6} />
            </label>
            <label>
              カテゴリー
              <input name="category" required />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              発行主体
              <input name="issuer" required />
            </label>
            <label>
              出典URL
              <input name="sourceUrl" required type="url" />
            </label>
            <div className="form-grid compact-grid">
              <label>
                公表日
                <input defaultValue={today} name="publishedOn" required type="date" />
              </label>
              <label>
                確認日
                <input defaultValue={today} name="checkedOn" required type="date" />
              </label>
              <label>
                有効開始
                <input defaultValue={today} name="effectiveFrom" required type="date" />
              </label>
              <label>
                有効期限
                <input defaultValue={nextYear} name="validUntil" required type="date" />
              </label>
            </div>
            <label>
              備考
              <textarea name="notes" rows={3} />
            </label>
            <button disabled={busy}>下書き登録</button>
          </form>
          <ContentRecords
            empty="最新安全情報は未登録です。"
            records={latest.map((item) => ({
              id: item.id,
              title: item.title,
              meta: `${item.issuer} · 有効期限 ${item.validUntil}`,
              status: item.approvalStatus,
              action: (status) =>
                run(async () => {
                  await transitionLatestSafetyUpdate(item.id, status, csrfToken);
                }, '最新安全情報の状態を更新しました。'),
            }))}
            busy={busy}
          />
        </div>
      )}

      {section === 'source-watch' && (
        <div className="stack">
          <div className="notice warning">
            Source
            Registryで取得元・用途・商用利用条件を先に固定します。変更は自動採用せず、必ず確認待ちになります。AIによる自動要約は行いません。
          </div>
          <div className="notice">
            HTML・RSS・Atom・PDFメタデータを優先度と確認時刻に従って監視します。PDF本文・画像・HTML全文は保存しません。手動索引は承認できますが、自動取得は行いません。
          </div>
          <div className="subtab-list" aria-label="公的情報監視の画面">
            <button
              className={`subtab ${sourceWatchView === 'registry' ? 'active' : ''}`}
              onClick={() => setSourceWatchView('registry')}
              type="button"
            >
              Source Registry・監視履歴
            </button>
            <button
              className={`subtab ${sourceWatchView === 'review' ? 'active' : ''}`}
              onClick={() => setSourceWatchView('review')}
              type="button"
            >
              新着・審査待ち
            </button>
            <button
              className={`subtab ${sourceWatchView === 'permission' ? 'active' : ''}`}
              onClick={() => setSourceWatchView('permission')}
              type="button"
            >
              利用許諾・教材候補
            </button>
          </div>
          {sourceWatchView === 'registry' && (
            <>
              <div className="content-grid">
                <form
                  className="card stack"
                  onSubmit={(event) => void submitSourceWatchSource(event)}
                >
                  <h3>Source Registryへ公式ソースを登録</h3>
                  <label>
                    管理用タイトル
                    <input name="title" placeholder="厚生労働省 労働災害情報" required />
                  </label>
                  <label>
                    組織名
                    <input name="organization" placeholder="厚生労働省" required />
                  </label>
                  <label>
                    発行主体
                    <input name="issuer" placeholder="厚生労働省" required />
                  </label>
                  <label>
                    Registryグループコード
                    <input name="registryGroupCode" placeholder="MHLW_SAFETY" required />
                  </label>
                  <label>
                    カテゴリー（1行1項目）
                    <textarea
                      name="categories"
                      placeholder={'労働災害\n注意喚起'}
                      required
                      rows={3}
                    />
                  </label>
                  <div className="form-grid compact-grid">
                    <label>
                      ソース区分
                      <select defaultValue="OFFICIAL_GOV" name="sourceClass">
                        <option value="OFFICIAL_GOV">国の公式機関</option>
                        <option value="OFFICIAL_LOCAL_GOV">地方公共団体</option>
                        <option value="INDUSTRY_ASSOCIATION">業界団体</option>
                        <option value="PROFESSIONAL_ASSOCIATION">専門団体</option>
                        <option value="REFERENCE">参考情報</option>
                      </select>
                    </label>
                    <label>
                      対象地域
                      <select defaultValue="NATIONAL" name="regionScope">
                        <option value="NATIONAL">全国</option>
                        <option value="HOKKAIDO">北海道</option>
                        <option value="OTHER_REGION">その他地域</option>
                        <option value="MULTI_REGION">複数地域</option>
                      </select>
                    </label>
                    <label>
                      優先度
                      <select defaultValue="NORMAL" name="priority">
                        <option value="CRITICAL">最重要（1〜24時間）</option>
                        <option value="HIGH">高（24〜72時間）</option>
                        <option value="NORMAL">通常（24〜168時間）</option>
                        <option value="REFERENCE">参考（168〜720時間）</option>
                      </select>
                    </label>
                    <label>
                      確認間隔（時間）
                      <input
                        defaultValue="24"
                        max="720"
                        min="1"
                        name="checkFrequencyHours"
                        required
                        type="number"
                      />
                    </label>
                  </div>
                  <label>
                    組織の基本URL（公開https）
                    <input
                      name="baseUrl"
                      placeholder="https://www.mhlw.go.jp/"
                      required
                      type="url"
                    />
                  </label>
                  <label>
                    公式ページURL（公開https）
                    <input name="sourceUrl" required type="url" />
                  </label>
                  <div className="form-grid compact-grid">
                    <label>
                      取得方式
                      <select defaultValue="HTML_HASH" name="watchMethod">
                        <option value="HTML_HASH">HTML差分監視</option>
                        <option value="RSS">RSS</option>
                        <option value="ATOM">Atom</option>
                        <option value="PDF_METADATA">PDFメタデータ（本文は取得しない）</option>
                        <option value="MANUAL_INDEX">手動索引（自動取得しない）</option>
                      </select>
                    </label>
                    <label>
                      教材利用方針
                      <select defaultValue="FACT_SOURCE" name="contentUsagePolicy">
                        <option value="FACT_SOURCE">事実根拠</option>
                        <option value="CASE_SOURCE">事例根拠</option>
                        <option value="STRUCTURE_REFERENCE">構成参考</option>
                        <option value="REFERENCE_ONLY">閲覧のみ</option>
                        <option value="MANUAL_PERMISSION_REQUIRED">個別許諾が必要</option>
                      </select>
                    </label>
                    <label>
                      商用利用確認
                      <select defaultValue="NEEDS_REVIEW" name="commercialUseStatus">
                        <option value="CONFIRMED">確認済み</option>
                        <option value="NEEDS_REVIEW">要確認</option>
                        <option value="RESTRICTED">制限あり</option>
                        <option value="PROHIBITED">禁止</option>
                      </select>
                    </label>
                    <label>
                      robots・取得方針
                      <select defaultValue="UNCONFIRMED" name="robotsPolicyStatus">
                        <option value="UNCONFIRMED">未確認</option>
                        <option value="ALLOWED">取得可を確認</option>
                        <option value="MANUAL_REVIEW">管理者判断済み</option>
                        <option value="DISALLOWED">取得不可</option>
                      </select>
                    </label>
                  </div>
                  <label className="checkbox-row">
                    <input name="permissionRequired" type="checkbox" />
                    教材利用前に個別許諾が必要
                  </label>
                  <label>
                    許諾・利用条件メモ
                    <textarea name="permissionNotes" rows={3} />
                  </label>
                  <button disabled={busy}>下書き登録</button>
                </form>
                <div className="stack">
                  {sourceWatchSources.length === 0 ? (
                    <div className="card empty-state">監視ソースは未登録です。</div>
                  ) : (
                    sourceWatchSources.map((source) => (
                      <article className="card stack" key={source.id}>
                        <div className="content-row">
                          <div>
                            <strong>{source.title}</strong>
                            <small>
                              {source.organization ?? source.issuer} · {source.hostname}
                            </small>
                          </div>
                          <StatusAction
                            busy={busy}
                            onAction={(status) =>
                              run(async () => {
                                await transitionSourceWatchSource(source.id, status, csrfToken);
                              }, '監視ソースの状態を更新しました。')
                            }
                            status={source.approvalStatus}
                          />
                        </div>
                        <a href={source.sourceUrl} rel="noreferrer" target="_blank">
                          公式ページを確認
                        </a>
                        <small>
                          Registry: {source.registryGroupCode ?? '未設定'} ·{' '}
                          {source.sourceClass ?? '区分未設定'} · {source.priority ?? '優先度未設定'}{' '}
                          · {source.watchMethod ?? '取得方式未設定'} /{' '}
                          {source.checkFrequencyHours ?? '-'}
                          時間
                        </small>
                        <small>
                          カテゴリー: {source.categories.join(' / ')} · 利用方針:{' '}
                          {source.contentUsagePolicy ?? '未設定'} · 商用利用:{' '}
                          {source.commercialUseStatus ?? '未設定'} · robots:{' '}
                          {source.robotsPolicyStatus ?? '未設定'}
                        </small>
                        {source.registryConfigComplete !== 1 && (
                          <div className="notice warning">
                            旧形式のソースです。Registry項目が未完了のため承認・監視できません。
                          </div>
                        )}
                        <small>
                          最終確認: {source.lastCheckedAt ?? '未実施'} · 変更検知:{' '}
                          {source.lastChangedAt ?? 'なし'} · 連続失敗 {source.consecutiveFailures}回
                        </small>
                        <small>
                          最終成功: {source.lastSuccessAt ?? '未実施'} · 次回確認:{' '}
                          {source.nextCheckAt ?? (source.pausedAt ? '自動停止中' : '未設定')}
                        </small>
                        {source.pausedAt !== null && (
                          <div className="notice error">
                            自動停止: {source.pausedReason ?? '取得先の応答を確認してください。'}
                          </div>
                        )}
                        {source.lastErrorCode !== null && (
                          <div className="notice error">
                            {source.lastErrorCode}: {source.lastErrorDetail}
                          </div>
                        )}
                        {source.pausedAt !== null && (
                          <form
                            className="stack"
                            onSubmit={(event) => {
                              event.preventDefault();
                              const reason = text(
                                new FormData(event.currentTarget),
                                'resumeReason',
                              );
                              void run(async () => {
                                await resumeSourceWatchSource(source.id, reason, csrfToken);
                              }, '監視を再開しました。次回確認時刻から取得を再試行します。');
                            }}
                          >
                            <label>
                              再開理由（原因確認・対応内容）
                              <textarea minLength={10} name="resumeReason" required rows={2} />
                            </label>
                            <button className="secondary" disabled={busy} type="submit">
                              自動監視を再開
                            </button>
                          </form>
                        )}
                        {source.approvalStatus === 'APPROVED' &&
                          source.checkEnabled === 1 &&
                          source.pausedAt === null &&
                          source.watchMethod !== 'MANUAL_INDEX' && (
                            <button
                              className="secondary"
                              disabled={busy}
                              onClick={() =>
                                void run(async () => {
                                  await checkSourceWatchSource(source.id, csrfToken);
                                }, '公式ページを確認しました。変更があれば確認待ちへ追加されています。')
                              }
                              type="button"
                            >
                              今すぐ更新確認
                            </button>
                          )}
                      </article>
                    ))
                  )}
                </div>
              </div>

              <div className="stack">
                <div className="section-heading">
                  <div>
                    <h3>監視バッチ履歴</h3>
                    <p className="muted">
                      対象件数・処理件数・失敗・時間制限で次回へ繰り越した件数を記録します。
                    </p>
                  </div>
                </div>
                {sourceWatchBatches.length === 0 ? (
                  <div className="card empty-state">監視バッチはまだ実行されていません。</div>
                ) : (
                  sourceWatchBatches.map((batch) => (
                    <article className="card content-row" key={batch.id}>
                      <div>
                        <strong>{batch.status}</strong>
                        <small>
                          {batch.startedAt} · 対象 {batch.eligibleCount}件 / 処理{' '}
                          {batch.processedCount}件
                        </small>
                        <small>
                          成功 {batch.successCount} · 失敗 {batch.failureCount} · 新規{' '}
                          {batch.newItemCount} · 繰越 {batch.deferredCount}
                        </small>
                      </div>
                    </article>
                  ))
                )}
              </div>
            </>
          )}

          {sourceWatchView === 'review' && (
            <SourceReviewPanel
              busy={busy}
              csrfToken={csrfToken}
              equipmentTypes={equipmentTypes}
              items={sourceWatchItems}
              onError={onError}
              onRefresh={refresh}
              onRun={run}
              sources={sourceWatchSources}
              topics={topics}
              workTypes={workTypes}
            />
          )}
          {sourceWatchView === 'permission' && (
            <SourcePermissionPanel
              busy={busy}
              csrfToken={csrfToken}
              decisions={sourcePermissionDecisions}
              items={sourceWatchItems}
              onRun={run}
              sources={sourceWatchSources}
            />
          )}
        </div>
      )}

      {section === 'knowledge' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitKnowledgePack(event)}>
            <h3>Knowledge Pack versionを登録</h3>
            <p className="muted">
              事故パターン、原因、役割、KY、確認問題、条件差分を構造化JSONで登録します。登録後は内容を直接変更せず、新しいversionを作成します。
            </p>
            <label>
              登録先
              <select name="packId">
                <option value="">新しいKnowledge Pack</option>
                {[...new Map(knowledgePacks.map((item) => [item.packId, item])).values()].map(
                  (item) => (
                    <option key={item.packId} value={item.packId}>
                      {item.code}（{item.topicName}）
                    </option>
                  ),
                )}
              </select>
            </label>
            <label>
              新規コード
              <input name="code" placeholder="DUMP_REVERSING_CORE" />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              version JSON
              <textarea
                name="versionJson"
                placeholder="右側の既存versionにある『改訂用JSON』をコピーし、内容を修正して登録できます。"
                required
                rows={18}
              />
            </label>
            <div className="notice warning">
              法令・数値・公的情報を含める場合は、先に承認済み安全情報を登録し、sourcesでそのIDを参照してください。
            </div>
            <button disabled={busy}>不変versionを下書き登録</button>
          </form>
          <div className="stack">
            {knowledgePacks.length === 0 ? (
              <div className="card empty-state">Knowledge Packは未登録です。</div>
            ) : (
              knowledgePacks.map((pack) => (
                <article className="card stack" key={pack.id}>
                  <div className="content-row">
                    <div>
                      <strong>
                        {pack.title}（{pack.code} v{pack.versionNo}）
                      </strong>
                      <small>
                        {pack.topicName} · {pack.contentHash.slice(0, 12)}…
                      </small>
                    </div>
                    <StatusAction
                      busy={busy}
                      onAction={(status) =>
                        run(async () => {
                          await transitionThemeKnowledgePackVersion(
                            pack.packId,
                            pack.id,
                            status,
                            csrfToken,
                          );
                        }, 'Knowledge Packの状態を更新しました。')
                      }
                      status={pack.approvalStatus}
                    />
                  </div>
                  <p>{pack.themeOverview}</p>
                  <p className="muted">{pack.whyItMatters}</p>
                  <div className="content-metrics">
                    <span>知識項目 {pack.items.length}</span>
                    <span>KY {pack.kyScenarios.length}</span>
                    <span>確認問題 {pack.quizBank.length}</span>
                    <span>条件差分 {pack.variations.length}</span>
                    <span>承認済み出典 {pack.sources.length}</span>
                  </div>
                  <details>
                    <summary>全内容を確認</summary>
                    <div className="stack">
                      {knowledgePackSections.map(([sectionCode, label]) => {
                        const items = pack.items.filter((item) => item.section === sectionCode);
                        if (items.length === 0) return null;
                        return (
                          <section key={sectionCode}>
                            <h4>{label}</h4>
                            <ul>
                              {items.map((item) => (
                                <li key={item.code}>
                                  <strong>{item.title}</strong>：{item.body}
                                  {item.conditionCode ? `（条件: ${item.conditionCode}）` : ''}
                                </li>
                              ))}
                            </ul>
                          </section>
                        );
                      })}
                      <section>
                        <h4>構造化KY</h4>
                        {pack.kyScenarios.map((scenario) => (
                          <details key={scenario.code}>
                            <summary>{scenario.title}</summary>
                            <p>{scenario.situation}</p>
                            <p>問いかけ: {scenario.prompt}</p>
                            <p>停止条件: {scenario.stopCondition}</p>
                            <p>模範ポイント: {scenario.modelPoints.join('／')}</p>
                          </details>
                        ))}
                      </section>
                      <section>
                        <h4>確認問題バンク</h4>
                        {pack.quizBank.map((question) => (
                          <details key={question.code}>
                            <summary>
                              {question.question}（{question.type}）
                            </summary>
                            <p>正解: {question.correctAnswer}</p>
                            <p>解説: {question.explanation}</p>
                          </details>
                        ))}
                      </section>
                      <section>
                        <h4>季節・作業条件差分</h4>
                        <ul>
                          {pack.variations.map((variation) => (
                            <li key={`${variation.type}-${variation.key}`}>
                              <strong>{variation.title}</strong>：{variation.guidance.join('／')}
                            </li>
                          ))}
                        </ul>
                      </section>
                    </div>
                  </details>
                  <details>
                    <summary>改訂用JSON</summary>
                    <textarea
                      aria-label={`${pack.title}の改訂用JSON`}
                      readOnly
                      rows={20}
                      value={JSON.stringify(knowledgePackVersionInput(pack), null, 2)}
                    />
                  </details>
                </article>
              ))
            )}
          </div>
        </div>
      )}

      {section === 'diagrams' && (
        <div className="content-grid diagram-library-grid">
          <form className="card stack" onSubmit={(event) => void submitDiagramAsset(event)}>
            <h3>SVG図解versionを登録</h3>
            <p className="muted">
              16:9の内部SVGを、用途・注釈位置・権利情報と一緒に登録します。登録済みの内容は直接変更せず、新しいversionを作成します。
            </p>
            <label>
              登録先
              <select name="assetId">
                <option value="">新しい図解</option>
                {[...new Map(diagramAssets.map((item) => [item.assetId, item])).values()].map(
                  (item) => (
                    <option key={item.assetId} value={item.assetId}>
                      {item.code}（{item.topicName}）
                    </option>
                  ),
                )}
              </select>
            </label>
            <label>
              新規コード
              <input name="code" placeholder="dump_new_diagram.svg" />
            </label>
            <label>
              互換alias（1行1件、任意）
              <textarea name="aliases" placeholder="旧diagram_key.svg" rows={3} />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              version JSON
              <textarea
                name="versionJson"
                placeholder="右側の『改訂用JSON』をコピーし、内部SVGパスや注釈情報を修正してください。"
                required
                rows={20}
              />
            </label>
            <div className="notice warning">
              assetPathは /slide-assets/diagrams/ 配下のSVGだけを指定できます。外部SVG
              URL、script、HTML埋め込みは使用しません。
            </div>
            <button disabled={busy}>不変versionを下書き登録</button>
          </form>
          <div className="stack">
            {diagramAssets.length === 0 ? (
              <div className="card empty-state">SVG図解は未登録です。</div>
            ) : (
              diagramAssets.map((asset) => (
                <article className="card stack diagram-asset-card" key={asset.id}>
                  <div className="content-row">
                    <div>
                      <strong>
                        {asset.title}（{asset.code} v{asset.versionNo}）
                      </strong>
                      <small>
                        {asset.topicName} · {asset.diagramType} · {asset.variant} ·{' '}
                        {asset.contentHash.slice(0, 12)}…
                      </small>
                    </div>
                    <StatusAction
                      busy={busy}
                      onAction={(status) =>
                        run(async () => {
                          await transitionDiagramAssetVersion(
                            asset.assetId,
                            asset.id,
                            status,
                            csrfToken,
                          );
                        }, '図解の状態を更新しました。')
                      }
                      status={asset.approvalStatus}
                    />
                  </div>
                  <a
                    aria-label={`${asset.title}のSVGを原寸で確認`}
                    className="diagram-preview"
                    href={asset.assetPath}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <img alt={asset.altText} loading="lazy" src={asset.assetPath} />
                  </a>
                  <p>{asset.purpose}</p>
                  <div className="content-metrics diagram-meta">
                    <span>{asset.aspectRatio}</span>
                    <span>注釈 {asset.annotationAnchors.length}</span>
                    <span>alias {asset.aliases.length}</span>
                    <span>{asset.rightsNote}</span>
                  </div>
                  {asset.aliases.length > 0 && <small>互換alias: {asset.aliases.join('／')}</small>}
                  <details>
                    <summary>注釈anchorと代替テキスト</summary>
                    <p>{asset.altText}</p>
                    <ul>
                      {asset.annotationAnchors.map((anchor) => (
                        <li key={anchor.code}>
                          {anchor.label}（{anchor.kind} / x:{anchor.x} y:{anchor.y}）
                        </li>
                      ))}
                    </ul>
                  </details>
                  <details>
                    <summary>改訂用JSON</summary>
                    <textarea
                      aria-label={`${asset.title}の改訂用JSON`}
                      readOnly
                      rows={18}
                      value={JSON.stringify(diagramVersionInput(asset), null, 2)}
                    />
                  </details>
                </article>
              ))
            )}
          </div>
        </div>
      )}

      {section === 'modules' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitModule(event)}>
            <h3>教材モジュールversionを登録</h3>
            <label>
              登録先
              <select name="moduleId">
                <option value="">新しいモジュール</option>
                {modules.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code}（{item.moduleType}）
                  </option>
                ))}
              </select>
            </label>
            <label>
              新規コード
              <input name="code" />
            </label>
            <label>
              種別
              <select name="moduleType" defaultValue="PREVENTION">
                {[
                  'INTRODUCTION',
                  'LEGAL_BASELINE',
                  'ACCIDENT_CASE',
                  'HAZARD_FACTOR',
                  'PREVENTION',
                  'SITE_PHOTO',
                  'CASE_STUDY',
                  'KY_QUESTION',
                  'TEST_QUESTION',
                  'SUMMARY',
                  'DISCLAIMER',
                ].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              タイトル
              <input name="title" required />
            </label>
            <label>
              本文
              <textarea name="body" required rows={8} />
            </label>
            <label>
              speaker notes
              <textarea name="speakerNotes" required rows={5} />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              KY設問（1行1問）
              <textarea name="kyQuestions" rows={4} />
            </label>
            <div className="notice warning">
              教材全体で8問以上必要です。通常モジュールには各2問を登録し、免責など問題が不要なモジュールは空欄にしてください。
            </div>
            {[1, 2].map((questionNo) => (
              <fieldset className="stack" key={questionNo}>
                <legend>確認問題 {questionNo}（任意）</legend>
                <label>
                  設問
                  <textarea maxLength={1000} name={`testQuestion${questionNo}Question`} rows={2} />
                </label>
                <label>
                  正解
                  <input maxLength={500} name={`testQuestion${questionNo}CorrectAnswer`} />
                </label>
                <label>
                  誤答（1行1件、最大5件）
                  <textarea name={`testQuestion${questionNo}WrongAnswers`} rows={3} />
                </label>
                <label>
                  解説
                  <textarea
                    maxLength={2000}
                    name={`testQuestion${questionNo}Explanation`}
                    rows={3}
                  />
                </label>
              </fieldset>
            ))}
            <div className="checkbox-grid">
              <label>
                <input name="required" type="checkbox" />
                必須モジュール
              </label>
              <label>
                <input name="containsLegalClaim" type="checkbox" />
                法令内容を含む
              </label>
              <label>
                <input name="containsNumericClaim" type="checkbox" />
                数値を含む
              </label>
            </div>
            <label>
              出典区分
              <select name="sourceType">
                <option value="INTERNAL_EDITORIAL">One Color編集</option>
                <option value="PUBLIC_SOURCE">公的情報</option>
              </select>
            </label>
            <label>
              発行主体
              <input name="issuer" />
            </label>
            <label>
              根拠資料
              <input name="sourceDocument" />
            </label>
            <label>
              出典URL
              <input name="sourceUrl" type="url" />
            </label>
            <div className="form-grid compact-grid">
              <label>
                確認日
                <input name="verifiedOn" type="date" />
              </label>
              <label>
                有効開始
                <input defaultValue={today} name="effectiveFrom" required type="date" />
              </label>
              <label>
                有効終了
                <input defaultValue={nextYear} name="effectiveTo" type="date" />
              </label>
            </div>
            <button disabled={busy}>不変versionを登録</button>
          </form>
          <ContentRecords
            empty="version登録済みモジュールはありません。"
            records={moduleVersions.map((item) => ({
              id: item.id,
              title: `${item.title}（${item.code} v${item.versionNo}）`,
              meta: `${item.moduleType} · ${item.contentHash.slice(0, 12)}…`,
              status: item.approvalStatus,
              action: (status) =>
                run(async () => {
                  await transitionTrainingModuleVersion(item.moduleId, item.id, status, csrfToken);
                }, 'モジュールの状態を更新しました。'),
            }))}
            busy={busy}
          />
        </div>
      )}

      {section === 'variants' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitVariant(event)}>
            <h3>A〜Eパターンを登録</h3>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              パターン
              <select name="code">
                {['A', 'B', 'C', 'D', 'E'].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              名称
              <input name="name" required />
            </label>
            <label>
              説明
              <textarea name="description" required rows={4} />
            </label>
            <label>
              導入方法
              <input name="introductionStyle" required />
            </label>
            <label>
              重点リスク（1行1項目）
              <textarea name="focusRisks" required rows={4} />
            </label>
            <label>
              スライドslot順（1行1種別）
              <textarea
                defaultValue={
                  'INTRODUCTION\nHAZARD_FACTOR\nPREVENTION\nKY_QUESTION\nTEST_QUESTION\nSUMMARY'
                }
                name="slotOrder"
                required
                rows={7}
              />
            </label>
            <div className="form-grid compact-grid">
              <label>
                最小枚数
                <input defaultValue="25" min="1" name="minimumSlides" required type="number" />
              </label>
              <label>
                最大枚数
                <input defaultValue="35" min="1" name="maximumSlides" required type="number" />
              </label>
            </div>
            <label>
              notes調子
              <select name="notesTone">
                <option value="FACILITATIVE">問いかけ型</option>
                <option value="DIRECT">直接説明型</option>
                <option value="CASE_BASED">事例型</option>
              </select>
            </label>
            <label>
              素材配分
              <select name="mediaPolicy">
                <option value="BALANCED">均衡</option>
                <option value="SITE_PHOTO_PRIORITY">現場写真優先</option>
                <option value="ILLUSTRATION_PRIORITY">図解優先</option>
              </select>
            </label>
            <label>
              事故事例カテゴリー
              <textarea name="accidentCaseCategories" rows={2} />
            </label>
            <label>
              KYカテゴリー
              <textarea name="kyQuestionCategories" rows={2} />
            </label>
            <label>
              テストカテゴリー
              <textarea name="testQuestionCategories" rows={2} />
            </label>
            <button disabled={busy}>下書き登録</button>
          </form>
          <div className="stack">
            <VariantWarnings coverage={coverage} />
            <ContentRecords
              empty="教材パターンは未登録です。"
              records={variants.map((item) => ({
                id: item.id,
                title: `${item.topicName} ${item.code}：${item.name}`,
                meta: item.description,
                status: item.status,
                action: (status) =>
                  run(async () => {
                    await transitionMaterialVariant(item.id, status, csrfToken);
                  }, '教材パターンの状態を更新しました。'),
              }))}
              busy={busy}
            />
          </div>
        </div>
      )}

      {section === 'media' && (
        <div className="content-grid">
          <form className="card stack" onSubmit={(event) => void submitMedia(event)}>
            <h3>共通素材を登録</h3>
            <p className="muted">
              JPEG・PNG・WebPまたは短尺MP4（25MB以下）。private
              R2へ保存し、承認後だけ生成候補にします。
            </p>
            <label>
              素材種別
              <select name="assetType">
                <option value="ILLUSTRATION">図解</option>
                <option value="PHOTO">共通写真</option>
                <option value="VIDEO">短尺動画</option>
              </select>
            </label>
            <label>
              タイトル
              <input name="title" required />
            </label>
            <label>
              カテゴリー
              <input name="category" required />
            </label>
            <TopicSelect name="topicId" topics={topics} />
            <label>
              出典URL（任意）
              <input name="sourceUrl" type="url" />
            </label>
            <label>
              権利・利用条件
              <textarea name="licenseNotes" required rows={4} />
            </label>
            <label>
              素材ファイル
              <input
                accept="image/jpeg,image/png,image/webp,video/mp4"
                name="file"
                required
                type="file"
              />
            </label>
            <button disabled={busy}>検証して登録</button>
          </form>
          <div className="stack">
            {media.length === 0 ? (
              <div className="card empty-state">登録済みの共通素材はありません。</div>
            ) : (
              media.map((asset) => (
                <article className="card content-row" key={asset.id}>
                  <div>
                    <strong>{asset.title ?? asset.id}</strong>
                    <small>
                      {asset.assetType} · {asset.category ?? '未分類'} ·{' '}
                      {(asset.sizeBytes / 1024 / 1024).toFixed(1)}MB
                    </small>
                    <a href={mediaLibraryAssetUrl(asset.id)} rel="noreferrer" target="_blank">
                      実ファイルを確認
                    </a>
                  </div>
                  <StatusAction
                    busy={busy}
                    onAction={(status) =>
                      run(async () => {
                        await transitionMediaLibraryAsset(asset.id, status, csrfToken);
                      }, '共通素材の状態を更新しました。')
                    }
                    status={asset.reviewStatus ?? 'DRAFT'}
                  />
                </article>
              ))
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function TopicSelect({ name, topics }: { name: string; topics: SafetyTopic[] }) {
  return (
    <label>
      利用可能テーマ
      <select name={name} required>
        <option value="">選択してください</option>
        {topics
          .filter((topic) => topic.status !== 'RETIRED')
          .map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.name}
            </option>
          ))}
      </select>
    </label>
  );
}

function ContentRecords({
  busy,
  empty,
  records,
}: {
  busy: boolean;
  empty: string;
  records: Array<{
    action: (status: ContentStatus) => Promise<void>;
    id: string;
    meta: string;
    status: ContentStatus;
    title: string;
  }>;
}) {
  if (records.length === 0) return <div className="card empty-state">{empty}</div>;
  return (
    <div className="stack">
      {records.map((record) => (
        <article className="card content-row" key={record.id}>
          <div>
            <strong>{record.title}</strong>
            <small>{record.meta}</small>
          </div>
          <StatusAction busy={busy} onAction={record.action} status={record.status} />
        </article>
      ))}
    </div>
  );
}

function VariantWarnings({ coverage }: { coverage: VariantCoverage[] }) {
  const warnings = coverage.filter((item) => item.missingApprovedCodes.length > 0);
  return (
    <div className={warnings.length === 0 ? 'notice success' : 'notice warning'}>
      {warnings.length === 0
        ? '全テーマでA〜Eの承認済みパターンが揃っています。'
        : `承認済みA〜Eが不足しているテーマ: ${warnings.length}件`}
      {warnings.slice(0, 5).map((item) => (
        <div key={item.topicId}>
          {item.topicName}: {item.missingApprovedCodes.join('・')}
        </div>
      ))}
    </div>
  );
}
